import {
  INVITATION_TOKEN_KEY,
  INVITATION_TYPE_KEY,
} from '@core/livestream/constants'

import type { ILiveStreamGlobalStateSlice } from './slice'
import { LIVESTREAM_SLICE_KEY } from './slice'
import type { IChannelUsername } from './state'

/**
 *
 */
export function heartbeatIntervalSelector(state: ILiveStreamGlobalStateSlice) {
  return state[LIVESTREAM_SLICE_KEY].heartbeatInterval
}

/**
 *
 */
export function joinedTopicsSelector(state: ILiveStreamGlobalStateSlice) {
  return state[LIVESTREAM_SLICE_KEY].joinedTopics
}

/**
 * @returns livestream model actions
 */
export function liveStreamActionsSelector(state: ILiveStreamGlobalStateSlice) {
  return state[LIVESTREAM_SLICE_KEY].actions
}

/**
 * @returns An active invitation token if it matches the video Id and has not
 *      been dismissed. Undefined otherwise.
 */
export function invitationTokenSelector(liveStreamId?: string) {
  return function (state: ILiveStreamGlobalStateSlice): string | undefined {
    if (!liveStreamId) {
      return
    }
    const { invitations, invitationsTokensDismissed: dismissedInvitations } =
      state[LIVESTREAM_SLICE_KEY]
    const invitationToken = invitations[liveStreamId]?.[INVITATION_TOKEN_KEY]
    if (
      invitationToken &&
      !dismissedInvitations[liveStreamId]?.[invitationToken]
    ) {
      return invitationToken
    }
  }
}

/**
 * @returns invitation token type for a given livestream id
 */
export function invitationTokenTypeSelector(liveStreamId?: string) {
  return function (state: ILiveStreamGlobalStateSlice) {
    if (!liveStreamId) {
      return
    }
    const { invitations, invitationsTokensDismissed: dismissedInvitations } =
      state[LIVESTREAM_SLICE_KEY]
    const invitationToken = invitations[liveStreamId]?.[INVITATION_TOKEN_KEY]
    const invitationType = invitations[liveStreamId]?.[INVITATION_TYPE_KEY]
    if (
      invitationToken &&
      !dismissedInvitations[liveStreamId]?.[invitationToken]
    ) {
      return invitationType
    }
  }
}

const defaultUsername = {
  name: '',
  isUserDefined: false,
}
/**
 * @returns channelUsername object for a given channel id
 */
export function usernameSelector(
  state: ILiveStreamGlobalStateSlice,
): IChannelUsername {
  // CS-3000: livestream slice can be undefined for unknown reasons.
  return (
    state[LIVESTREAM_SLICE_KEY]?.usernameHydrated ??
    (state[LIVESTREAM_SLICE_KEY]?.username || defaultUsername)
  )
}

/**
 *
 * @param state LiveStreamGlobalStateSlice
 * @returns IChannelUsername object for a given channel id
 */
export function usernameHydratedSelector(
  state: ILiveStreamGlobalStateSlice,
): IChannelUsername {
  return state[LIVESTREAM_SLICE_KEY]?.usernameHydrated ?? defaultUsername
}

/**
 *
 * @returns boolean representing whether or not the username was hydrated via widget param
 */
export function hasHydratedUsernameSelector(
  state: ILiveStreamGlobalStateSlice,
) {
  return !!state[LIVESTREAM_SLICE_KEY]?.usernameHydrated
}

/**
 * @returns persisted hearts count for a given livestream id
 */
export function heartsCountSelector(
  state: ILiveStreamGlobalStateSlice,
  liveStreamId: string | undefined,
): number {
  if (!liveStreamId) {
    return 0
  }
  return state[LIVESTREAM_SLICE_KEY]?.heartsCount?.[liveStreamId] ?? 0
}

/**
 * @returns "active" user state for a given livestream id
 */
export function activeWatchSelector(
  state: ILiveStreamGlobalStateSlice,
  liveStreamId: string | undefined,
): boolean {
  if (!liveStreamId) {
    return false
  }
  // CS-3000: livestream slice can be undefined for unknown reasons.
  return Boolean(state[LIVESTREAM_SLICE_KEY]?.activeWatches[liveStreamId])
}

/**
 * @returns true if any livestream is active
 */
export function anyActiveWatchSelector(state: ILiveStreamGlobalStateSlice) {
  return Object.values(state[LIVESTREAM_SLICE_KEY]?.activeWatches ?? {}).some(
    (active) => active,
  )
}

/**
 * Selector to get all giveaways for a livestream
 */
export function liveStreamGiveawaysSelector(
  state: ILiveStreamGlobalStateSlice,
  liveStreamId: string | undefined,
) {
  if (!liveStreamId) {
    return null
  }

  return state[LIVESTREAM_SLICE_KEY].liveStreamGiveaways[liveStreamId]
}

/**
 * Selector to get whether or not current user has entered a livestream's giveaway
 */
export function liveStreamGiveawaySelector(
  state: ILiveStreamGlobalStateSlice,
  liveStreamId: string | undefined,
  interactionId: string | undefined,
) {
  if (!(liveStreamId && interactionId)) {
    return null
  }
  return state[LIVESTREAM_SLICE_KEY].liveStreamGiveaways[liveStreamId]?.[
    interactionId
  ]
}
